import React, { useRef, useEffect, useState, useCallback } from "react";
import { FeatureGroup } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "leaflet-draw/dist/leaflet.draw.css";

// --- Fix Leaflet Icons ---
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});
const defaultPin = new L.Icon.Default();

export default function UserVectorLeaflet({ latestSurvey, apiBase }) {
  const featureGroupRef = useRef(null);
  const layersCache = useRef({}); 

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vectorName, setVectorName] = useState("");
  const [tempLayerData, setTempLayerData] = useState(null); 

  const getToken = () => localStorage.getItem("token");
  const slugify = (s) => (s ? s.toString().replace(/[^a-z0-9]/gi, "-").toLowerCase() : "");

  // =========================================================
  // 1. ROBUST LOAD LOGIC (Promise.all + AbortController)
  // =========================================================
  const loadVectors = useCallback(async (signal) => {
    if (!latestSurvey || !featureGroupRef.current) return;

    const locName = latestSurvey.location_name || latestSurvey.name;
    const survTime = latestSurvey.survey_time || latestSurvey.survey_date;

    if (!locName || !survTime) return;

    try {
        // --- A. Discovery ---
        const discoveryUrl = new URL(`${apiBase}/user_vectors/discovery`);
        discoveryUrl.searchParams.append("location_name", locName);
        discoveryUrl.searchParams.append("survey_time", survTime);

        const res = await fetch(discoveryUrl, {
           headers: { "Authorization": `Bearer ${getToken()}` },
           signal: signal // Pass abort signal
        });
        
        if (!res.ok) return; 
        const data = await res.json();
        const foundItems = data.found || [];

        // --- B. Fetch ALL GeoJSONs in parallel (Don't touch map yet) ---
        const locSlug = slugify(locName);
        const svSlug = slugify(survTime.includes("T") ? survTime.split("T")[0] : survTime);
        
        const fetchPromises = foundItems.map(async (item) => {
            let fetchUrl = `${apiBase}/user_vectors/${locSlug}/${svSlug}`;
            if (item.layer) fetchUrl += `/${item.layer}`; 
            fetchUrl += `/${item.itemname}`;

            try {
                const geoRes = await fetch(fetchUrl, {
                    headers: { "Authorization": `Bearer ${getToken()}` },
                    signal: signal
                });
                if (geoRes.ok) {
                    return { 
                        item: item, 
                        geoJson: await geoRes.json(), 
                        url: fetchUrl 
                    };
                }
            } catch (err) {
                if (err.name !== 'AbortError') console.warn("Failed to load:", item.itemname);
            }
            return null;
        });

        // Wait for all downloads to finish
        const results = await Promise.all(fetchPromises);
        const validResults = results.filter(r => r !== null);

        // --- C. CRITICAL: Check if aborted before updating map ---
        if (signal.aborted) return;

        // --- D. ATOMIC UPDATE (Clear then Add) ---
        featureGroupRef.current.clearLayers();
        layersCache.current = {};
        const loadedLayersForSidebar = [];

        validResults.forEach(({ item, geoJson, url }) => {
            const leafletLayer = L.geoJSON(geoJson, {
                style: { color: "blue", weight: 4, opacity: 0.6, fillOpacity: 0.2 },
                
                // Circle Fix
                pointToLayer: (feature, latlng) => {
                    if (feature.properties && feature.properties.radius) {
                        return L.circle(latlng, { 
                            radius: feature.properties.radius,
                            color: "blue", weight: 4, opacity: 0.6, fillOpacity: 0.2
                        });
                    }
                    return L.marker(latlng, { icon: defaultPin });
                },

                onEachFeature: (feature, layer) => {
                    layer.bindPopup(`<b>${item.itemname}</b>`);
                    layer._leaflet_id_server = item.itemname; 
                }
            });

            leafletLayer.eachLayer((l) => {
                l._leaflet_id_server = item.itemname; 
                featureGroupRef.current.addLayer(l);
                layersCache.current[item.itemname] = l;
            });

            loadedLayersForSidebar.push({
                id: item.itemname,
                name: item.itemname,
                visible: true,
                meta: { url }
            });
        });

        // --- E. Notify Sidebar ---
        window.dispatchEvent(new CustomEvent("user-vectors-discovered", { 
            detail: { layers: loadedLayersForSidebar } 
        }));

    } catch (err) {
        if (err.name !== 'AbortError') {
            console.error("[UserVector] Load error:", err);
        }
    }
  }, [latestSurvey, apiBase]);

  // =========================================================
  // 2. EFFECT: Trigger Load on Survey Change
  // =========================================================
  useEffect(() => {
    const controller = new AbortController();
    loadVectors(controller.signal);
    return () => controller.abort(); // Cancel previous fetch if user switches survey fast
  }, [loadVectors]);


  // =========================================================
  // 3. SIDEBAR INTERACTION
  // =========================================================
  useEffect(() => {
    const handleMapToggle = (e) => {
        const { id, visible } = e.detail;
        if (layersCache.current[id]) {
            const layer = layersCache.current[id];
            if (visible) {
                if (!featureGroupRef.current.hasLayer(layer)) featureGroupRef.current.addLayer(layer);
            } else {
                if (featureGroupRef.current.hasLayer(layer)) featureGroupRef.current.removeLayer(layer);
            }
        }
    };

    const handleMapDelete = async (e) => {
        const { id } = e.detail;
        if (layersCache.current[id]) {
            featureGroupRef.current.removeLayer(layersCache.current[id]);
            delete layersCache.current[id];
        }

        const locName = latestSurvey?.location_name || latestSurvey?.name;
        const survTime = latestSurvey?.survey_time || latestSurvey?.survey_date;
        const locSlug = slugify(locName);
        const svSlug = slugify(survTime && survTime.includes("T") ? survTime.split("T")[0] : survTime);

        try {
            await fetch(`${apiBase}/user_vectors/${locSlug}/${svSlug}/${id}`, {
                method: "DELETE",
                headers: { "Authorization": `Bearer ${getToken()}` }
            });
        } catch (err) { console.error("Delete failed", err); }
    };

    window.addEventListener("map-toggle-layer", handleMapToggle);
    window.addEventListener("map-remove-user-vector", handleMapDelete);
    return () => {
        window.removeEventListener("map-toggle-layer", handleMapToggle);
        window.removeEventListener("map-remove-user-vector", handleMapDelete);
    };
  }, [latestSurvey, apiBase]);


  // =========================================================
  // 4. CREATE / SAVE
  // =========================================================
  const _onCreated = (e) => {
    // Note: Leaflet Draw adds it to map automatically. We do NOT addLayer here.
    setTempLayerData({ layer: e.layer, type: e.layerType });
    setVectorName(""); 
    setIsModalOpen(true); 
  };

  const handleSaveVector = async () => {
    if (!tempLayerData || !vectorName.trim()) {
        alert("Please enter a name.");
        return;
    }

    const { layer, type } = tempLayerData;
    const geoJson = layer.toGeoJSON();

    // Circle Radius Fix
    if (type === 'circle' || (layer instanceof L.Circle)) {
        const radius = layer.getRadius();
        if (!geoJson.properties) geoJson.properties = {};
        geoJson.properties.radius = radius; 
    }

    layer.bindPopup(`<b>${vectorName}</b>`);
    layer.openPopup();

    const payload = {
      type: type,
      filename: vectorName,
      geometry: geoJson,
      location_name: latestSurvey?.location_name || latestSurvey?.name,
      survey_time: latestSurvey?.survey_time || latestSurvey?.survey_date
    };

    try {
      const response = await fetch(`${apiBase}/user_vectors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        console.log("Saved vector:", vectorName);
        // 1. Remove the "temp" drawing
        featureGroupRef.current.removeLayer(layer);
        // 2. Reload official data from server
        const controller = new AbortController();
        loadVectors(controller.signal);
      } else {
        alert("Failed to save.");
      }
    } catch (err) {
      console.error("API Error:", err);
    }

    setIsModalOpen(false);
    setTempLayerData(null);
  };

  const handleCancelVector = () => {
    if (tempLayerData && featureGroupRef.current) {
        featureGroupRef.current.removeLayer(tempLayerData.layer);
    }
    setIsModalOpen(false);
    setTempLayerData(null);
  };

  // =========================================================
  // 5. EDIT & DELETE
  // =========================================================
  const _onEdited = async (e) => {
    const layers = e.layers;
    layers.eachLayer(async (layer) => {
      const filename = layer._leaflet_id_server;
      if (!filename) return;

      const geoJson = layer.toGeoJSON();
      if (layer instanceof L.Circle) {
          if (!geoJson.properties) geoJson.properties = {};
          geoJson.properties.radius = layer.getRadius();
      }

      const payload = {
        location_name: latestSurvey?.location_name || latestSurvey?.name,
        survey_time: latestSurvey?.survey_time || latestSurvey?.survey_date,
        geometry: geoJson,
        filename: filename
      };

      try {
        await fetch(`${apiBase}/user_vectors`, {
          method: "POST", 
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
          body: JSON.stringify(payload),
        });
      } catch (err) { console.error("Update failed", err); }
    });
  };

  const _onDeleted = async (e) => {
    const layers = e.layers;
    layers.eachLayer(async (layer) => {
      const filename = layer._leaflet_id_server;
      if (!filename) return; // Local layer only

      // Optimistically remove from cache
      delete layersCache.current[filename];

      const locName = latestSurvey?.location_name || latestSurvey?.name;
      const survTime = latestSurvey?.survey_time || latestSurvey?.survey_date;
      const locSlug = slugify(locName);
      const svSlug = slugify(survTime && survTime.includes("T") ? survTime.split("T")[0] : survTime);

      try {
        await fetch(`${apiBase}/user_vectors/${locSlug}/${svSlug}/${filename}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${getToken()}` }
        });
        
        // Refresh to stay in sync
        const controller = new AbortController();
        loadVectors(controller.signal);
      } catch (err) { console.error("Delete failed", err); }
    });
  };

  return (
    <>
      <FeatureGroup ref={featureGroupRef}>
        <EditControl
          position="topright"
          onCreated={_onCreated}
          onEdited={_onEdited}
          onDeleted={_onDeleted}
          draw={{
            polygon: true,
            marker: { icon: defaultPin },
            polyline: { shapeOptions: { color: "blue", weight: 4 }, showLength: true, metric: true },
            rectangle: true,
            circle: true,
            circlemarker: false,
          }}
          edit={{ edit: true, remove: true }}
        />
      </FeatureGroup>

      {isModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", width: "300px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                <h3 style={{ marginTop: 0 }}>Name this Vector</h3>
                <input type="text" value={vectorName} onChange={(e) => setVectorName(e.target.value)} placeholder="Vector Name" style={{ width: "100%", padding: "8px", marginBottom: "15px", border: "1px solid #ccc", borderRadius: "4px" }} autoFocus />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button onClick={handleCancelVector} style={{ padding: "8px 12px", background: "#f3f4f6", border: "none", borderRadius: "4px", cursor: "pointer" }}>Cancel</button>
                    <button onClick={handleSaveVector} style={{ padding: "8px 12px", background: "#2563eb", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>Save</button>
                </div>
            </div>
        </div>
      )}
    </>
  );
}